import type { DateParts } from "./pattern";

export const DEFAULT_SITE_TIME_ZONE = "Asia/Shanghai";

export const SITE_TIME_ZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "Asia/Shanghai", label: "中国标准时间（北京 UTC+8）" },
  { value: "Asia/Hong_Kong", label: "香港时间（UTC+8）" },
  { value: "Asia/Taipei", label: "台北时间（UTC+8）" },
  { value: "Asia/Macau", label: "澳门时间（UTC+8）" },
  { value: "Asia/Singapore", label: "新加坡时间（UTC+8）" },
  { value: "Asia/Tokyo", label: "日本时间（UTC+9）" },
  { value: "Asia/Seoul", label: "韩国时间（UTC+9）" },
  { value: "Asia/Bangkok", label: "曼谷时间（UTC+7）" },
  { value: "Asia/Jakarta", label: "雅加达时间（UTC+7）" },
  { value: "Asia/Kolkata", label: "印度时间（UTC+5:30）" },
  { value: "Asia/Dubai", label: "迪拜时间（UTC+4）" },
  { value: "Europe/Moscow", label: "莫斯科时间（UTC+3）" },
  { value: "Europe/Paris", label: "中欧时间（巴黎 / 柏林）" },
  { value: "Europe/London", label: "英国时间（伦敦）" },
  { value: "UTC", label: "协调世界时（UTC）" },
  { value: "America/Sao_Paulo", label: "巴西时间（圣保罗）" },
  { value: "America/New_York", label: "美国东部时间（纽约）" },
  { value: "America/Chicago", label: "美国中部时间（芝加哥）" },
  { value: "America/Denver", label: "美国山地时间（丹佛）" },
  { value: "America/Los_Angeles", label: "美国太平洋时间（洛杉矶）" },
  { value: "Australia/Sydney", label: "澳大利亚东部时间（悉尼）" },
  { value: "Pacific/Auckland", label: "新西兰时间（奥克兰）" },
];

const OPTION_VALUES = new Set(SITE_TIME_ZONE_OPTIONS.map((item) => item.value));

export function isSiteTimeZone(value: unknown): value is string {
  return typeof value === "string" && OPTION_VALUES.has(value);
}

export function getSiteTimeZoneLabel(value: string): string {
  return (
    SITE_TIME_ZONE_OPTIONS.find((item) => item.value === value)?.label ?? value
  );
}

const SITE_TZ_ATTR = "data-site-tz";

function normalizeTimeZone(raw: string | undefined | null): string {
  const trimmed = (raw ?? "").trim();

  return isSiteTimeZone(trimmed) ? trimmed : DEFAULT_SITE_TIME_ZONE;
}

let runtimeSiteTimeZone: string | null = null;

export function setRuntimeSiteTimeZone(zone: string | null): void {
  runtimeSiteTimeZone = zone === null ? null : normalizeTimeZone(zone);
}

export function getSiteTimeZone(): string {
  if (typeof document !== "undefined") {
    return normalizeTimeZone(document.documentElement.getAttribute(SITE_TZ_ATTR));
  }

  return runtimeSiteTimeZone ?? DEFAULT_SITE_TIME_ZONE;
}

export function siteTimeZoneAttrs(zone: string): Record<string, string> {
  return { [SITE_TZ_ATTR]: normalizeTimeZone(zone) };
}

function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  build: (timeZone: string) => Intl.DateTimeFormat,
): Intl.DateTimeFormat {
  const zone = getSiteTimeZone();
  const hit = cache.get(zone);

  if (hit) {
    return hit;
  }

  const created = build(zone);
  cache.set(zone, created);

  return created;
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

export function siteWallClock(date: Date): DateParts {
  const map: Record<string, string> = {};
  const formatter = cachedFormatter(
    partsCache,
    (timeZone) =>
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
  );

  for (const part of formatter.formatToParts(date)) {
    if (part.type !== "literal") {
      map[part.type] = part.value;
    }
  }

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

const weekdayCache = new Map<string, Intl.DateTimeFormat>();

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function siteWeekday(date: Date): number {
  const text = cachedFormatter(
    weekdayCache,
    (timeZone) =>
      new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }),
  ).format(date);

  return WEEKDAY_INDEX[text] ?? 0;
}

function siteOffsetMs(date: Date): number {
  const parts = siteWallClock(date);
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  return asIfUtc - (date.getTime() - date.getMilliseconds());
}

export function instantFromSiteWallClock(parts: DateParts): Date {
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  if (Number.isNaN(asIfUtc)) {
    return new Date(Number.NaN);
  }

  const firstGuess = asIfUtc - siteOffsetMs(new Date(asIfUtc));

  return new Date(asIfUtc - siteOffsetMs(new Date(firstGuess)));
}

export function siteNowAsWallClockDate(): Date {
  const parts = siteWallClock(new Date());

  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
}
